import {
  IConversationMessage,
  IMasterDataUser,
  ITicketConversationEdit,
  ITicketMessage,
  ITicketMessageGroup,
  ProfileButtonColors,
} from '../../models';
import { toLocalDayKey } from '../../utils/dates';

export const ticketChatConverter = {
  fromDto: (
    ticketMessages: IConversationMessage[],
    user: IMasterDataUser
  ): ITicketMessageGroup[] => {
    let messageGroups: ITicketMessageGroup[] = [];
    const colors = Object.values(ProfileButtonColors);
    const idToColorMap: { [id: string]: string } = {};
    if (ticketMessages?.length > 0) {
      messageGroups = ticketMessages.reduce((result, current) => {
        let msg: ITicketMessage = {
          id: current.id,
          message: current.message,
          attachments: current.attachments,
          timestamp: Number(current.created.date),
          isIncoming: current.created.editor.name !== user.firstName + ' ' + user.lastName,
        };
        if (!msg.isIncoming) {
          msg = {
            ...msg,
            status: current.status,
            retryMessageId: current.retryMessageId,
            retriedOn: current.retriedOn,
          };
        }
        const dayKey = toLocalDayKey(msg.timestamp);
        const lastGroup = result[result.length - 1];
        if (
          lastGroup &&
          lastGroup.user.name === current.created.editor.name &&
          lastGroup.dayKey === dayKey
        ) {
          lastGroup.messages.push(msg);
        } else {
          if (msg.isIncoming) {
            // eslint-disable-next-line no-prototype-builtins
            if (!idToColorMap.hasOwnProperty(current.created.editor.name)) {
              idToColorMap[current.created.editor.name] =
                colors[Object.keys(idToColorMap).length % colors.length];
            }
          }
          result.push({
            user: current.created.editor,
            userInitials: msg.isIncoming
              ? current.created.editor?.name
                  ?.split(' ')
                  .map(word => word[0])
                  .join('')
                  .substring(0, 2) || ''
              : user.initials.substring(0, 2),
            isIncoming: msg.isIncoming,
            messages: [msg],
            color: idToColorMap[current.created.editor.name],
            dayKey,
          });
        }

        return result;
      }, []);
    }
    return messageGroups;
  },
  toNewMessage: (
    sentMessageId: string,
    sentMessage: ITicketConversationEdit,
    ticketMessages: ITicketMessageGroup[],
    user: IMasterDataUser,
    internalTicketing: boolean
  ): ITicketMessageGroup[] => {
    if (ticketMessages?.length > 0) {
      const now = Date.now();
      const todayKey = toLocalDayKey(now);
      const lastMessageGroup = {
        ...ticketMessages[ticketMessages.length - 1],
        messages: [...ticketMessages[ticketMessages.length - 1].messages],
      };
      if (!lastMessageGroup.isIncoming && lastMessageGroup.dayKey === todayKey) {
        lastMessageGroup.messages.push({
          id: sentMessageId,
          message: sentMessage.message,
          attachments: sentMessage.attachments,
          timestamp: now,
          isIncoming: false,
          status: internalTicketing ? 'ACKNOWLEDGED' : 'PENDING',
        });
        ticketMessages[ticketMessages.length - 1] = lastMessageGroup;
      } else {
        ticketMessages.push(
          ticketChatConverter.toNewMessageGroup(user, sentMessage, sentMessageId, internalTicketing)
        );
      }
    } else {
      ticketMessages = [
        ticketChatConverter.toNewMessageGroup(user, sentMessage, sentMessageId, internalTicketing),
      ];
    }
    return ticketMessages;
  },
  toNewMessageGroup: (
    user: IMasterDataUser,
    message: ITicketConversationEdit,
    messageId: string,
    internalTicketing: boolean
  ): ITicketMessageGroup => {
    const now = Date.now();
    return {
      user: { id: user.id, name: user.firstName + user.lastName },
      userInitials: user.initials,
      isIncoming: false,
      messages: [
        {
          id: messageId,
          message: message.message,
          attachments: message.attachments,
          timestamp: now,
          isIncoming: false,
          status: internalTicketing ? 'ACKNOWLEDGED' : 'PENDING',
        },
      ],
      dayKey: toLocalDayKey(now),
    };
  },
  hasPendingMessage: (messageGroup: ITicketMessageGroup[]): boolean => {
    return !!messageGroup
      .findLast(group => !group.isIncoming)
      .messages.findLast(message => message.status === 'PENDING');
  },
  appendNewMessagesToChat(
    oldChat: readonly ITicketMessageGroup[],
    newChat: readonly ITicketMessageGroup[]
  ): ITicketMessageGroup[] {
    const oldMsgs = oldChat.flatMap(g => g.messages);
    const newMsgs = newChat.flatMap(g => g.messages);
    const oldMsgIds = new Set(oldMsgs.map(m => m.id));

    const trulyNewMsgs = newMsgs.filter(m => !oldMsgIds.has(m.id)).filter(m => m.isIncoming);
    if (trulyNewMsgs.length === 0) return oldChat.slice();

    const result: ITicketMessageGroup[] = oldChat.length > 0 ? [...oldChat] : [];

    for (const msg of trulyNewMsgs) {
      const msgDayKey = toLocalDayKey(msg.timestamp);
      const lastGroup = result[result.length - 1];
      const lastGroupUserId = lastGroup?.user?.id;

      if (
        lastGroup &&
        lastGroup.isIncoming === msg.isIncoming &&
        lastGroupUserId !== undefined &&
        lastGroup.dayKey === msgDayKey
      ) {
        result[result.length - 1] = {
          ...lastGroup,
          messages: [...lastGroup.messages, msg],
        };
      } else {
        const lastIncomingGroup = [...result].reverse().find(g => g.isIncoming);

        // Find the group in newChat that contains this message for user info
        const sourceGroup = newChat.find(group => group.messages.some(m => m.id === msg.id));
        // Use lastIncomingGroup if available, otherwise use sourceGroup (for first incoming message)
        const userInfo = lastIncomingGroup || sourceGroup;

        result.push({
          user: userInfo?.user,
          userInitials: userInfo?.userInitials,
          isIncoming: msg.isIncoming,
          messages: [msg],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          color: userInfo?.color || (msg as any).color,
          dayKey: msgDayKey,
        });
      }
    }
    return result;
  },
};
